// buttcrbti.js
import React, { useState, useEffect } from 'react';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import { db } from './firebase';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

function getTradeR(trade) {
  if (trade.outcome === 'Stop Loss') return -1;
  if (trade.outcome === 'Take Profit') {
    if (trade.tpMilestones && trade.tpMilestones.length > 0) {
      let maxR = 0;
      trade.tpMilestones.forEach((milestone) => {
        if (milestone.status === 'הושג') {
          const parts = milestone.tp.split(':');
          if (parts.length === 2) {
            const ratio = parseFloat(parts[1]);
            if (!isNaN(ratio) && ratio > maxR) {
              maxR = ratio;
            }
          }
        }
      });
      return maxR;
    }
    return 1;
  }
  return 0;
}

const ButtCrbtiStats = () => {
  const [trades, setTrades] = useState([]);
  const triggerName = 'BUTTC/RBTI';

  useEffect(() => {
    const tradesRef = collection(db, 'trades');
    const q = query(tradesRef, orderBy('date', 'asc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const allTrades = [];
      snapshot.forEach((docSnap) => {
        allTrades.push({ id: docSnap.id, ...docSnap.data() });
      });
      // סינון עסקאות לטריגר BUTTC/RBTI
      const filtered = allTrades.filter((t) => t.matchedTrigger === triggerName);
      setTrades(filtered);
    });
    return () => unsubscribe();
  }, []);

  // חישובי סטטיסטיקות
  const totalTrades = trades.length;
  let sumR = 0;
  let winCount = 0;
  trades.forEach((t) => {
    const r = getTradeR(t);
    sumR += r;
    if (t.outcome === 'Take Profit') winCount++;
  });
  const avgR = totalTrades ? sumR / totalTrades : 0;
  const winRate = totalTrades ? (winCount / totalTrades) * 100 : 0;

  const cumulativeData = (() => {
    if (!trades.length) return [];
    const sorted = [...trades].sort((a, b) => new Date(a.date) - new Date(b.date));
    let cum = 0;
    return sorted.map((t) => {
      cum += getTradeR(t);
      return { date: t.date, cumulativeRR: cum };
    });
  })();

  const outcomeCounts = trades.reduce((acc, t) => {
    const outcome = t.outcome || 'לא ידוע';
    acc[outcome] = (acc[outcome] || 0) + 1;
    return acc;
  }, {});
  const pieData = Object.entries(outcomeCounts).map(([name, value]) => ({ name, value }));
  const pieColors = ['#0088FE', '#00C49F', '#FF8042', '#FFBB28'];

  return (
    <Box sx={{ direction: 'rtl', p: 3 }}>
      <Typography variant="h4" gutterBottom align="center">
        סטטיסטיקות טריגר: {triggerName}
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ boxShadow: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" align="center">
                סה"כ עסקאות
              </Typography>
              <Typography variant="h5" align="center">
                {totalTrades}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ boxShadow: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" align="center">
                סה"כ R
              </Typography>
              <Typography variant="h5" align="center">
                {sumR.toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ boxShadow: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" align="center">
                ממוצע RR
              </Typography>
              <Typography variant="h5" align="center">
                {avgR.toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ boxShadow: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" align="center">
                WIN RATE
              </Typography>
              <Typography variant="h5" align="center">
                {winRate.toFixed(2)}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card sx={{ boxShadow: 3, p: 2 }}>
            <Typography variant="h6" gutterBottom align="center">
              RR מצטבר
            </Typography>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={cumulativeData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="cumulativeRR" stroke="#8884d8" name="Cumulative RR" />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{ boxShadow: 3, p: 2 }}>
            <Typography variant="h6" gutterBottom align="center">
              חלוקת תוצאות
            </Typography>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={pieColors[index % pieColors.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ButtCrbtiStats;
